/**
 * Timeline relationships hooks
 * Manages user relationship data (following/followers)
 */
'use client';

import {
  TimelineRelationshipsRequestSchema,
  type TimelineRelationshipsResponse,
  TimelineRelationshipsResponseSchema,
} from '@sweetistics/contracts';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { trpc } from '@/lib/trpc/client';
import { wrapTrpcMutation } from '@/lib/trpc/mutation-utils';
import { extractTrpcHttpStatus } from './trpc-error-helpers';

// Hook to fetch relationship status for multiple profiles
export function useUserRelationships(
  profileIds: string[],
  enabled = true
): UseQueryResult<TimelineRelationshipsResponse> {
  const baseMutation = trpc.timeline.relationships.useMutation({
    trpc: { abortOnUnmount: true },
  });

  const mutation = wrapTrpcMutation(
    baseMutation,
    (response) => TimelineRelationshipsResponseSchema.parse(response),
    (error) => {
      const trpcError = error instanceof TRPCClientError ? error : null;
      const httpStatus = trpcError ? extractTrpcHttpStatus(trpcError) : null;
      if (httpStatus === 401) {
        return new Error('Authentication required to load relationship data.');
      }
      if (httpStatus === 429) {
        return new Error('Too many relationship lookups. Please try again later.');
      }
      return error instanceof Error ? error : new Error('Failed to load relationship data');
    }
  );

  return useQuery({
    queryKey: ['user-relationships', profileIds],
    queryFn: () => {
      const input = TimelineRelationshipsRequestSchema.parse({ profileIds });
      return mutation.mutateAsync(input);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}
